import React, { useMemo } from 'react';
import { GenericTable } from '../../GenericTable/GenericTable';
import { DoseRequiredRecord } from '../../../types/data';
import { getCurrentPlans } from '../../../utils/storage/currentPlans';
import { calculateDoseRequirements } from '../../../utils/calculations/doses-required';
import { VialSvg } from '../../Vial/Vial';
import { getHistory } from '../../../utils/storage/history';

interface RowDataItem extends DoseRequiredRecord {
  userAmount: number;
  userAmountUnits: string;
  source: 'history' | 'none';
  calculatedVials: number;
}

export const DosesTab: React.FC = () => {
  const history = getHistory();
  const currentPlans = getCurrentPlans().filter(plan => ['peptide', 'amino', 'coenzyme'].includes(plan.type))

  const calculateVialDimensions = (count: number) => {
    let groupSize = 5; // Default group size for counts <= 20
    if (count > 20) groupSize = 10; // Adjust group size for larger counts

    if (count <= 5) return { width: 50, height: 50, groupSize };
    if (count <= 10) return { width: 30, height: 30, groupSize };
    if (count <= 20) return { width: 20, height: 20, groupSize };
    if (count > 20) return { width: 10, height: 10, groupSize };
    return { width: 50, height: 50, groupSize }; // Default case
  };

  const doses = useMemo(() => calculateDoseRequirements(currentPlans), [currentPlans]);

  const findMatchingData = (name: string) => {
    const historyMatch = history.find((h) => h.inputs.peptideName === name);
    if (historyMatch) {
      return {
        amount: historyMatch.inputs.vialAmount,
        amountUnits: historyMatch.inputs.vialUnits,
        source: 'history',
      };
    }

    return { amount: 0, amountUnits: '', source: 'none' };
  };

  const calculateVialsNeeded = (
    vialAmount: number,
    vialUnits: string,
    totalAmount: number,
    totalUnits: string
  ): number => {
    if (vialAmount <= 0) return 0;

    const convertToMcg = (amount: number, unit: string): number => {
      switch (unit.toLowerCase()) {
        case 'ml':
          throw new Error('Cannot convert ml to mcg without concentration info');
        case 'mg':
          return amount * 1000;
        case 'mcg':
          return amount;
        default:
          throw new Error(`Unsupported unit: ${unit}`);
      }
    };

    try {
      const vialAmountMcg = convertToMcg(vialAmount, vialUnits);
      const totalAmountMcg = convertToMcg(totalAmount, totalUnits);
      return Math.ceil(totalAmountMcg / vialAmountMcg);
    } catch (error) {
      return 0;
    }
  };

  const rowData = useMemo(() => {
    // Group doses by name and aggregate totals
    const groupedDoses = doses.reduce((acc, dose) => {
      if (!acc[dose.name]) {
        const match = findMatchingData(dose.name);
        acc[dose.name] = {
          ...dose,
          totalAmount: 0, // Initialize aggregation
          totalVials: 0, // Initialize aggregation
          totalDoses: 0,
          userAmount: match.amount ?? 0,
          userAmountUnits: match.amountUnits ?? '',
          source: match.source,
        };
      }
      // Aggregate totalAmount and totalVials
      acc[dose.name].totalDoses += dose.totalDoses;
      acc[dose.name].totalAmount += dose.totalAmount;
      acc[dose.name].totalVials += dose.totalVials;
      return acc;
    }, {} as Record<string, RowDataItem>);

    // Add calculated vials needed for each grouped item
    Object.keys(groupedDoses).forEach((name) => {
      const item = groupedDoses[name];
      item.calculatedVials = calculateVialsNeeded(
        item.userAmount,
        item.userAmountUnits,
        item.totalAmount,
        item.totalAmountUnits
      );
    });

    return groupedDoses;
  }, [doses, history]);

  const tableData = useMemo(() => Object.values(rowData), [rowData]);

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Doses Required</h2>
      {doses.length === 0 ? (
        <p className="text-gray-500">No doses available. Add some plans to see requirements here.</p>
      ) : (
        <GenericTable
          data={tableData}
          columns={[
            { key: 'name', label: 'Name', sticky: 'left' },
            {
              key: 'vial',
              label: 'Vial',
              render: (item) => (
                <div className="flex space-x-2 items-center">
                  <span className={`${item.source === 'none' ? 'text-red-500' : ''}`}>
                    {item.source === 'none' ? 'No history' : `${item.userAmount} ${item.userAmountUnits}`}
                  </span>
                </div>
              ),
            },
            {
              key: 'required',
              label: 'Total',
              render: (item) => `${item.totalAmount} ${item.totalAmountUnits}`,
            },
            {
              key: 'doses',
              label: 'Total Doses',
              render: (item) => `${item.totalDoses}`,
            },
            {
              key: 'dateRange',
              label: 'Date Range',
              minWidth: '200px', // Bigger column for date ranges
              maxWidth: '250px',
              sticky: 'right',
              render: (item) => {
                const formatDate = (date) => {
                  try {
                    return new Intl.DateTimeFormat('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    }).format(new Date(date));
                  } catch {
                    return 'Invalid date';
                  }
                };
                return `${formatDate(item.startDate)} - ${formatDate(item.endDate)}`;
              },
            },
            {
              key: 'vials',
              label: 'Vials Needed',
              render: (item) => {
                const vialCount = item.calculatedVials;
                const { width, height, groupSize } = calculateVialDimensions(vialCount);

                if (vialCount <= 0) {
                  return <span className="text-red-500">No valid historical data</span>;
                }

                const groups = [];
                for (let i = 0; i < vialCount; i += groupSize) {
                  const group = Array.from(
                    { length: Math.min(groupSize, vialCount - i) },
                    (_, j) => i + j
                  );
                  groups.push(group);
                }

                return (
                  <div className="flex flex-col space-y-2">
                    <span className="text-gray-800 font-bold">{vialCount}x Vials</span>
                    {groups.map((group, groupIndex) => (
                      <div className="flex space-x-1 items-center" key={`group-${groupIndex}`}>
                        {group.map((index) => (
                          <VialSvg
                            amount={`${item.userAmount} ${item.userAmountUnits}`}
                            height={width}
                            width={height}
                            label={item.name}
                            key={`${item.name}-${index}`}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                );
              },
            },
          ]}
        />

      )}
    </div>
  );
};
